import {
  AppId,
  CheckEmailResponse as CheckEmail,
  checkEmail,
} from '@otter-ai/recall-client';
import { useMutation } from '@tanstack/react-query';
import { CODE_INVALID_EMAIL } from '#types';
import {
  createEmailCheckSignature,
  SESSION_STORAGE_KEYS,
  setPartialLoginState,
  setSessionItem,
} from '#utils';

// Type for the response structure returned by recallApi.checkEmail
export type CheckEmailApiResponse = {
  data?: CheckEmail;
  error?: {
    code?: number;
    message?: string;
  };
  request?: Request;
  response?: Response;
};

/**
 * Custom hook for calling the check email API
 * @returns A function to manually check an email, along with its loading state and whether the email is in use
 */
export const useCheckEmailMutation = () => {
  const mutation = useMutation<CheckEmailApiResponse, Error, string>({
    mutationFn: async (email: string) => {
      if (!email) throw new Error('Email is required');

      const signatureBody = await createEmailCheckSignature(email);

      const response = await checkEmail({
        query: { appid: process.env.NEXT_PUBLIC_APP_ID as AppId, email },
        body: { email, ...signatureBody },
      });

      return response;
    },
  });

  const checkEmailAsync = async (
    email: string,
  ): Promise<CheckEmailApiResponse> => {
    if (!email) throw new Error('Email is required');

    const response = await mutation.mutateAsync(email);
    setPartialLoginState({
      workspace: response.data?.workspace,
      isEmailInUse: response.data?.user_email,
    });
    setSessionItem(SESSION_STORAGE_KEYS.AUTH_EMAIL, email);
    return response;
  };

  const isEmailInUse = async (email: string): Promise<boolean> => {
    const responseBody = await checkEmailAsync(email);
    if (!responseBody.response || !responseBody.response.ok) {
      throw new Error(responseBody.response?.statusText ?? 'Unknown error');
    }
    if (responseBody.data?.user_email) return true;
    if (
      responseBody.error?.code === CODE_INVALID_EMAIL ||
      responseBody.data?.user_email === false
    )
      return false;
    throw new Error(responseBody.error?.message);
  };

  return {
    checkEmailAsync,
    isEmailInUse,
    isCheckingEmail: mutation.isPending,
    mutation,
  };
};
